import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {ActionButtonType} from '~core/enum';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {ActionButton, ActionButtonEvent, AuthenticatedUser, Club, Customer, Reminder, SidebarOptions} from '~core/models';
import {ClubService, NavigationService, ReminderService, ScreenSizeService, StorageService} from '~core/services';
import {BasicStatusTranslationService, TooltipTranslationService} from '~shared/translations';
import {UserReminder} from '~core/models/customer/user-reminder';

/**
 * Operate the reminders on the user level
 */
@UntilDestroy()
@Component({
  template: ''
})
export class HomeRemindersComponent implements OnDestroy {
  public currentReminders: Array<Reminder>;
  public loading: boolean = true;
  public authenticatedUser: AuthenticatedUser;
  public selectedClub: Club;
  public selectedCustomer: Customer;

  public i18nEmptyState: string = $localize`:@@customerOverviewComponent.text.noReminders:There are no reminders`;
  public i18nHeading: string;

  public baseApi: string;
  public customQueries: Array<any> = [];

  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];
  public actionButtons: Array<ActionButton> = [];

  public sidebarOpened: boolean = false;

  constructor(
    public clubService: ClubService,
    public reminderService: ReminderService,
    public basicStatusTranslationService: BasicStatusTranslationService,
    public route: ActivatedRoute,
    public storage: StorageService,
    public tablesTranslations: TablesTranslations,
    public sidebarService: SidebarService,
    public navigationService: NavigationService,
    public tooltipTranslations: TooltipTranslationService,
    public router: Router,
    public screenSizeService: ScreenSizeService,
  ) {
    this.navigationService.setCurrentPage('homeOpenReminders');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'homeOpenReminders']);
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => this.authenticatedUser = user);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe((club: Club) => this.selectedClub = club);
    this.reminderService.castReminderList.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data != null) {
          this.currentReminders = data;
        }
      });

    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
    }
  }

  public ngOnDestroy(): void {
  }

  /**
   * Sets the new reminder list
   */
  public createReminders(reminders: Array<UserReminder>): void {
    const data = this.reminderService.createUserRemindersClass(reminders);
    this.reminderService.setReminderList(data);
  }

  /**
   * Toggles sidebar modal opened or closed
   *
   * @param status true or false
   */
  public toggleSidebar(status: boolean): void {
    this.sidebarOpened = status;
  }

  /**
   * Opens the modal and removes lingering validation
   */
  public openEditReminder(reminder: any): void {
    this.selectedCustomer = reminder.customer;
    this.reminderService.announceCurrentReminder(reminder);
    this.toggleSidebar(true);
  }

  /**
   * Checks which action button was clicked
   */
  public actionButtonClicked(actionButton: ActionButtonEvent<Reminder>): void {
    if (actionButton.type === ActionButtonType.REDIRECT) {
      this.router.navigate(['club', this.selectedClub.id, 'customer', actionButton.data.customer.id]);
    } else if (actionButton.type === ActionButtonType.EDIT) {
      this.openEditReminder(actionButton.data);
    }
  }

  /**
   * Adds the columns and keys for the table
   */
  protected setUp(): void {
    this.columns.push(
      this.tablesTranslations.i18nDeadline,
      this.tablesTranslations.i18nCustomer,
      this.tablesTranslations.i18nComment,
      this.tablesTranslations.i18nStatus
    );
    this.keys.push(['deadline', 'fullInfo', 'text', 'status']);
    this.actionButtons.push(
      new ActionButton({
        type: ActionButtonType.REDIRECT,
        icon: '#esf-icon-user-small',
        tooltip: this.tooltipTranslations.i18nShowCustomer
      }),
      new ActionButton({
        type: ActionButtonType.EDIT,
        icon: '#esf-icon-edit',
        tooltip: this.tooltipTranslations.i18nEdit
      })
    );
  }

}
